import type { RefObject } from 'react';
import type { IntakeFormValues } from './_sections/IntakeFields';
import {
  validateIntakeFields,
  type IntakeFieldErrors,
} from './intake-validation';
import { focusFirstIntakeError } from './intake-focus';
import { submitIntakeLead } from './submit-intake-lead';
import { markIntakeCompleted } from './intake-storage';
import { readMclidCookie } from '../../sdk/mclid';

type Analytics = {
  trackValidationErrors: (errors: Record<string, string>) => void;
  trackSubmitted: (opts: {
    consentGiven: boolean;
    countryCodeOverridden: boolean;
  }) => void;
};

export async function submitIntakeForm(params: {
  values: IntakeFormValues & { prefillIso: string | null };
  engageId: string;
  sessionId: string;
  threadId: string;
  captureMclidCookie?: boolean;
  analytics: Analytics;
  formRef: RefObject<HTMLFormElement | null>;
  setErrors: (errors: IntakeFieldErrors) => void;
  /** Called with every field-error set shown (front validation or API `details`). */
  onFieldErrors?: (errors: IntakeFieldErrors) => void;
  setEmailSuggestion: (suggestion: string | null) => void;
  setSubmitError: (msg: string | null) => void;
  onCompleted: () => void;
}): Promise<boolean> {
  const {
    values,
    engageId,
    sessionId,
    threadId,
    captureMclidCookie = false,
    analytics,
    formRef,
    setErrors,
    onFieldErrors,
    setEmailSuggestion,
    setSubmitError,
    onCompleted,
  } = params;

  const {
    errors: nextErrors,
    e164Phone,
    emailSuggestion,
  } = validateIntakeFields(values);
  // Soft typo offer — never blocks submit when the address is otherwise valid.
  setEmailSuggestion(emailSuggestion);
  if (Object.keys(nextErrors).length > 0 || !e164Phone) {
    setErrors(nextErrors);
    onFieldErrors?.(nextErrors);
    analytics.trackValidationErrors(nextErrors as Record<string, string>);
    // Focus stays on Continue: the summary is announced from the host region.
    return false;
  }

  const countryCodeOverridden =
    Boolean(values.prefillIso) &&
    Boolean(values.phoneCountryIso) &&
    values.phoneCountryIso.toUpperCase() !==
      (values.prefillIso ?? '').toUpperCase();
  // SEM-7209: collect GUS Mclid at intake identify — before the first chat message.
  const cookieString = typeof document === 'undefined' ? '' : document.cookie;
  const mclid = captureMclidCookie ? readMclidCookie(cookieString) : undefined;
  const result = await submitIntakeLead({
    engageId,
    sessionId,
    threadId,
    data: {
      givenName: values.givenName.trim(),
      familyName: values.noFamilyName ? null : values.familyName.trim(),
      email: values.email.trim(),
      phone: e164Phone,
      currentCountryCode: values.currentCountryCode.trim().toUpperCase(),
      ...(values.marketingConsent ? { marketingConsent: true } : {}),
      countryCodeOverridden,
      ...(mclid ? { mclid } : {}),
    },
  });

  if (!result.ok) {
    // API safety net: a known field from `details` becomes a field error.
    if (Object.keys(result.fieldErrors).length > 0) {
      setErrors(result.fieldErrors);
      onFieldErrors?.(result.fieldErrors);
      focusFirstIntakeError(formRef.current, result.fieldErrors);
      return false;
    }
    setSubmitError(result.message || 'Something went wrong. Please try again.');
    return false;
  }

  markIntakeCompleted(engageId, sessionId);
  analytics.trackSubmitted({
    consentGiven: values.marketingConsent,
    countryCodeOverridden,
  });
  onCompleted();
  return true;
}
